/**
 * src/data/CultureVibeBank.js
 * Hardcoded scenario bank for Part 7: Decoding Culture.
 */
export const cultureVibeBank = [
  {
    "id": 1,
    "text": "'We're like a family here.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Families have unconditional love; corporations have conditional employment. This signals a lack of professional boundaries and emotional manipulation to justify unpaid labor."
  },
  {
    "id": 2,
    "text": "The hiring manager asks: 'What do you like to do outside of work?'",
    "correctId": "B",
    "feedback": "✅ Healthy. Demonstrates respect for human identity outside of operational output and supports psychological safety without prying into protected demographics."
  },
  {
    "id": 3,
    "text": "'We work hard, play hard.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Translation: 60-hour workweeks, extreme operational pressure, and mandatory social drinking events to mask systemic burnout."
  },
  {
    "id": 4,
    "text": "Your manager sends a non-urgent Slack DM at 9:30 PM on a Sunday.",
    "correctId": "A",
    "feedback": "🚩 Toxic. Violation of asynchronous boundaries. Normalizes persistent connectivity and creates cognitive friction during off-hours."
  },
  {
    "id": 5,
    "text": "The team lead publicly takes accountability for a delayed deployment during the department sync.",
    "correctId": "B",
    "feedback": "✅ Healthy. Extreme leadership ownership. Establishes a blameless culture focused on systemic process improvement over personal scapegoating."
  },
  {
    "id": 6,
    "text": "'We don't track PTO here—take whatever you need.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Unlimited PTO without mandatory minimums creates peer guilt and social pressure, statistically resulting in employees taking *fewer* days off than under accrual models."
  },
  {
    "id": 7,
    "text": "An engineering lead declines a meeting invite because it lacks a clear agenda or pre-read document.",
    "correctId": "B",
    "feedback": "✅ Healthy. Respect for deep-work focus and temporal efficiency. Prevents meeting bloat and enforces structured communication."
  },
  {
    "id": 8,
    "text": "'We need someone who is comfortable wearing many hats.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Code word for severe understaffing, scope creep, and chaotic role definitions without corresponding financial compensation."
  },
  {
    "id": 9,
    "text": "The CEO regularly yells in executive meetings, but leadership claims 'it's just because they are passionate.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Excusing verbal aggression as 'passion' destroys psychological safety and signals an unpunished, top-down hostile workplace culture."
  },
  {
    "id": 10,
    "text": "Your direct manager asks for your preferred feedback style (e.g., written, verbal, immediate vs. 1:1) during onboarding.",
    "correctId": "B",
    "feedback": "✅ Healthy. High emotional intelligence. Customizes communication channels to optimize individual growth and coachability."
  },
  {
    "id": 11,
    "text": "'If you're not failing, you're not trying hard enough.' (Used to dismiss broken production code).",
    "correctId": "A",
    "feedback": "🚩 Toxic. Weaponized growth-mindset jargon used to excuse sloppy execution, lack of QA infrastructure, and reckless operational risk."
  },
  {
    "id": 12,
    "text": "The VP explicitly mandates a 1-hour calendar block daily for 'No-Meeting Deep Work' across the department.",
    "correctId": "B",
    "feedback": "✅ Healthy. Protects cognitive retention ratios and respects the physical necessity of uninterrupted execution time."
  },
  {
    "id": 13,
    "text": "A peer asks: 'Are you sure you want to log off now? The rest of us are staying to finish the deck.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Peer-enforced guilt tripping and presenteeism. Rewards physical presence and martyrdom over task completion and efficiency."
  },
  {
    "id": 14,
    "text": "Post-mortems focus entirely on 'How did the system fail?' rather than 'Who broke the build?'",
    "correctId": "B",
    "feedback": "✅ Healthy. Blameless engineering posture. Isolates root-cause vulnerabilities in the pipeline rather than attacking individuals."
  },
  {
    "id": 15,
    "text": "'We are looking for a rockstar/ninja who thrives in chaos.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Indicates a lack of operational processes, high turnover, and an expectation that an individual will solo-carry broken infrastructure."
  },
  {
    "id": 16,
    "text": "The department head enforces a mandatory 24-hour response window on internal Slack messages.",
    "correctId": "B",
    "feedback": "✅ Healthy. De-escalates artificial urgency. Protects asynchronous workflows and prevents context-switching death spirals."
  },
  {
    "id": 17,
    "text": "Promotion decisions are made behind closed doors based on 'culture fit' rather than objective KPI rubrics.",
    "correctId": "A",
    "feedback": "🚩 Toxic. 'Culture fit' used as an un-auditable proxy for nepotism, unconscious bias, and political alignment."
  },
  {
    "id": 18,
    "text": "The company slides boast an average tenure of 8+ years across the entire leadership team.",
    "correctId": "A",
    "feedback": "🚩 Toxic. While long tenure can indicate stability, near-100% executive retention often signals an entrenched 'boys club', resistance to modern operational practices, and severe promotional ceilings for outside hires."
  },
  {
    "id": 19,
    "text": "During your interview, the hiring manager explicitly states: 'We don't expect answers after 6 PM, but we do expect tasks to be completed on time.'",
    "correctId": "B",
    "feedback": "✅ Healthy. Clear, output-oriented management. Prioritizes delivery metrics and task completion over performative presenteeism and constant digital availability."
  },
  {
    "id": 20,
    "text": "'We operate as a meritocracy where the best ideas always win, regardless of who proposed them.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Corporate idealism that masks structural power dynamics. Unstructured 'meritocracies' almost universally default to rewarding the loudest, most politically connected voices."
  },
  {
    "id": 21,
    "text": "Your direct manager explicitly schedules quarterly 'Career Architecture' reviews separate from performance and compensation reviews.",
    "correctId": "B",
    "feedback": "✅ Healthy. Decouples immediate operational execution from long-term professional development, demonstrating genuine investment in employee trajectory."
  },
  {
    "id": 22,
    "text": "'We need someone with thick skin who doesn't take things personally.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Translation: Unprofessional behavior, public shaming, and abusive management style are tolerated here, and you will be expected to endure it silently."
  },
  {
    "id": 23,
    "text": "The engineering team maintains a public, searchable wiki of all past architecture decisions and post-mortems.",
    "correctId": "B",
    "feedback": "✅ Healthy. High institutional transparency and knowledge democratization. Eliminates info-hoarding and reduces onboarding friction for new hires."
  },
  {
    "id": 24,
    "text": "'We don't like red tape or bureaucracy—we just break things and move fast.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Code for zero documentation, absence of standard operating procedures, technical debt overload, and reactive fire-fighting as a daily standard."
  },
  {
    "id": 25,
    "text": "During a team call, a director interrupts a junior employee to say: 'Let's pause, I want to hear [Junior's Name] finish their thought.'",
    "correctId": "B",
    "feedback": "✅ Healthy. High psychological safety and active inclusion. Leadership actively counters hierarchical dominance in group discussions."
  },
  {
    "id": 26,
    "text": "The company's Glassdoor reviews repeatedly mention: 'Great people in the trenches, but management is totally disconnected.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Indicates trauma-bonding among overworked peers fighting systemic executive incompetence and strategic drift."
  },
  {
    "id": 27,
    "text": "The VP of Product routinely asks during sprint planning: 'Which tickets can we drop to ensure the team doesn't burn out this cycle?'",
    "correctId": "B",
    "feedback": "✅ Healthy. Sustainable velocity management. Recognizes capacity limits and enforces realistic trade-offs between scope and employee well-being."
  },
  {
    "id": 28,
    "text": "'We expect complete agility—your priorities will change daily.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Lack of strategic direction from executive leadership masquerading as 'agility'. Results in wasted labor, whiplash, and chronic dissatisfaction."
  },
  {
    "id": 29,
    "text": "A project lead sends a message at 5 PM on Friday: 'Drafting this for Monday—do NOT look at or respond to this over the weekend.'",
    "correctId": "B",
    "feedback": "✅ Healthy. Active boundary enforcement. Sender takes explicit ownership to prevent artificial urgency and protect team off-time."
  },
  {
    "id": 30,
    "text": "You notice that every single Vice President in the department was hired externally within the last 6 months.",
    "correctId": "A",
    "feedback": "🚩 Toxic. Internal mobility vacuum. Proves the organization does not develop or promote internal talent, creating an insurmountable career ceiling for staff."
  },
  {
    "id": 31,
    "text": "The team uses an open, transparent pay scale matrix mapped directly to clear competency levels.",
    "correctId": "B",
    "feedback": "✅ Healthy. Transparent compensation models eliminate gender/racial pay gaps and reduce political maneuvering for raises."
  },
  {
    "id": 32,
    "text": "'We don't watch the clock, we just care about results.' (In an organization with no defined scope or deliverables).",
    "correctId": "A",
    "feedback": "🚩 Toxic. Exploitative paradox. Without bounded scope, 'focusing on results' becomes an excuse for endless, goalpost-shifting workloads that never end."
  },
  {
    "id": 33,
    "text": "When a major mistake happens, the department head asks: 'What gap in our training process allowed this to occur?'",
    "correctId": "B",
    "feedback": "✅ Healthy. Systems-thinking approach to failure. Focuses on organizational architecture and process improvement rather than personal punitive measures."
  },
  {
    "id": 34,
    "text": "Your team channel has a dedicated bot that posts praise when someone works past 10 PM.",
    "correctId": "A",
    "feedback": "🚩 Toxic. Systemic gamification of toxic work habits. Weaponizes peer recognition to glorify sleep deprivation and poor work-life balance."
  },
  {
    "id": 35,
    "text": "'We don't believe in micromanagement—we give you full autonomy.' (In an onboarding environment with zero documentation or training).",
    "correctId": "A",
    "feedback": "🚩 Toxic. Masking neglect as 'autonomy.' Throwing new hires into sink-or-swim environments without operational scaffolding leads directly to early tenure failure."
  },
  {
    "id": 36,
    "text": "The company provides a mandatory $1,500 annual stipend specifically dedicated to home office ergonomics and continuous education.",
    "correctId": "B",
    "feedback": "✅ Healthy. Tangible capital allocation toward employee wellness and professional growth, backing corporate values with real financial resources."
  },
  {
    "id": 37,
    "text": "'We need team players who don't care about title or job descriptions.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Exploitative boundary erosion. Prepares candidates to accept endless scope creep and under-compensated responsibilities without clear performance bounds."
  },
  {
    "id": 38,
    "text": "Your manager asks during your 1:1: 'What is one thing I am currently doing that is blocking your team's velocity?'",
    "correctId": "B",
    "feedback": "✅ Healthy. Upward accountability and high psychological safety. Demonstrates servant leadership and active removal of operational friction."
  },
  {
    "id": 39,
    "text": "'We are looking for someone who treats this company's budget like it's their own money.' (While offering below-market base compensation).",
    "correctId": "A",
    "feedback": "🚩 Toxic. Asymmetric risk alignment. Expects extreme psychological investment and financial frugality without offering equity or market-rate compensation."
  },
  {
    "id": 40,
    "text": "The company's internal Slack features active employee resource groups (ERGs) with direct executive sponsorship and dedicated operating budgets.",
    "correctId": "B",
    "feedback": "✅ Healthy. Structural commitment to diversity, equity, and inclusion rather than performative marketing copy."
  },
  {
    "id": 41,
    "text": "'Things move very fast here—if you need structure, you won't last.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Glorification of operational chaos. Normalizes reactive decision-making, systemic burnout, and poor executive leadership as an industry standard."
  },
  {
    "id": 42,
    "text": "An executive sends an email to the entire department praising a team for catching a bug early and delaying a release to protect quality.",
    "correctId": "B",
    "feedback": "✅ Healthy. Rewards psychological courage and long-term product integrity over short-term release metrics and performative speed."
  },
  {
    "id": 43,
    "text": "'We don't track hours, we track impact.' (In an organization where 'impact' is subjectively defined by how late you stay online).",
    "correctId": "A",
    "feedback": "🚩 Toxic. Performative flexibility. Replaces measurable working hours with subjective, political evaluations of 'dedication' and visibility."
  },
  {
    "id": 44,
    "text": "Your direct manager explicitly reminds you to turn on Slack 'Do Not Disturb' mode while you are on approved paid time off.",
    "correctId": "B",
    "feedback": "✅ Healthy. Active protection of rest and recovery cycles. Ensures true disconnection and prevents passive work-interruption bleed."
  },
  {
    "id": 45,
    "text": "'We pay in the 50th percentile, but our culture and perks make up for the rest.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Attempting to substitute low-cost office novelties (free snacks, ping pong tables) for competitive, inflation-adjusted liquid cash."
  },
  {
    "id": 46,
    "text": "During quarterly reviews, managers are evaluated on their team's retention rates and low burnout scores.",
    "correctId": "B",
    "feedback": "✅ Healthy. Aligns leadership incentives directly with human capital sustainability and psychological safety."
  },
  {
    "id": 47,
    "text": "'We expect 110% effort every single day.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Mathematically and humanly unsustainable. Running systems or people at 100%+ capacity continuously leads inevitably to catastrophic system failure."
  },
  {
    "id": 48,
    "text": "The leadership team publicly publishes the company's financial runway, burn rate, and strategic goals to all employees quarterly.",
    "correctId": "B",
    "feedback": "✅ Healthy. High radical transparency. Treats employees as trusted enterprise stakeholders rather than replaceable execution units."
  },
  {
    "id": 49,
    "text": "'We have a passion for excellence, so we don't tolerate excuses.'",
    "correctId": "A",
    "feedback": "🚩 Toxic. Punitive culture that reframes legitimate operational roadblocks, resource shortages, or systemic risks as personal failures."
  },
  {
    "id": 50,
    "text": "When a project hits a major roadblock, the team immediately schedules a blameless 'Five Whys' root-cause analysis session.",
    "correctId": "B",
    "feedback": "✅ Healthy. Continuous learning architecture. Treats friction as diagnostic data to improve operational workflows rather than placing personal blame."
  }
];